import React from "react";
import LegalPageLayout from "@user/components/LegalPageLayout";

/**
 * Privacy Policy — required for any Indian consumer-facing product handling
 * personal data. Written to be readable, and to align with the Digital
 * Personal Data Protection Act, 2023 (DPDP Act) plus the Information
 * Technology (Reasonable Security Practices) Rules, 2011.
 */
export default function PrivacyPolicy() {
  return (
    <LegalPageLayout title="Privacy Policy" lastUpdated="7 February 2026">
      <p>
        Advoset Technologies Private Limited (&quot;Advoset&quot;, &quot;we&quot;,
        &quot;us&quot;) respects your privacy. This Privacy Policy explains how we
        collect, use, share, and protect your personal data when you use the
        Advoset platform. It has been drafted with reference to the Digital
        Personal Data Protection Act, 2023 (&quot;DPDP Act&quot;) and the
        Information Technology (Reasonable Security Practices and Procedures
        and Sensitive Personal Data or Information) Rules, 2011.
      </p>

      <h2>1. Information we collect</h2>
      <ul>
        <li>
          <strong>Account information</strong> — name, phone number, email
          address, city, and the language you prefer.
        </li>
        <li>
          <strong>Verification information (lawyers only)</strong> — Bar Council
          enrolment number, PAN, address proof, and bank details for payouts.
        </li>
        <li>
          <strong>Consultation content</strong> — chat, audio, and video
          exchanged between clients and lawyers; documents you upload.
        </li>
        <li>
          <strong>Payment metadata</strong> — order IDs, transaction status,
          and last-four digits of your card. Full card numbers are handled
          directly by our RBI-authorised payment aggregator (Cashfree) and are
          never stored on Advoset&apos;s servers.
        </li>
        <li>
          <strong>Device &amp; usage data</strong> — IP address, device model,
          browser, and interaction logs, used for security and to improve the
          product.
        </li>
      </ul>

      <h2>2. How we use your data</h2>
      <ul>
        <li>To provide and improve the Advoset platform.</li>
        <li>
          To match you with a suitable lawyer and to facilitate your
          consultation.
        </li>
        <li>To process payments to lawyers and platform fees.</li>
        <li>
          To send transactional messages (booking confirmations, receipts,
          reminders) over email, SMS, and WhatsApp.
        </li>
        <li>
          To detect and prevent fraud, abuse, and unauthorised access.
        </li>
        <li>
          To comply with legal, regulatory, and law-enforcement obligations.
        </li>
      </ul>
      <p>
        We do <strong>not</strong> sell your personal data. We do not use the
        contents of your consultations to train third-party AI models.
      </p>

      <h2>3. Legal bases</h2>
      <p>
        We process your data (a) with your consent, (b) for the performance of
        our contract with you, (c) to comply with legal obligations, and (d) for
        our legitimate interests where these are not overridden by your rights.
      </p>

      <h2>4. Sharing your data</h2>
      <ul>
        <li>
          <strong>Lawyers</strong> — the lawyer you consult receives your name,
          contact information, and the content of your communications, to the
          extent needed to advise you.
        </li>
        <li>
          <strong>Payment aggregator</strong> — Cashfree receives the payment
          metadata needed to process transactions and payouts. Cashfree is a
          RBI-authorised payment aggregator.
        </li>
        <li>
          <strong>Cloud infrastructure &amp; communication providers</strong> —
          hosting, email delivery, and video call vendors under contractual
          confidentiality obligations.
        </li>
        <li>
          <strong>Legal &amp; regulatory disclosures</strong> — where compelled
          by a valid court order or statutory demand.
        </li>
      </ul>

      <h2>5. Data retention</h2>
      <p>
        We retain personal data for as long as your account is active and for
        the period thereafter required by law, tax, or dispute-resolution
        purposes. Consultation transcripts are retained for at least three
        years to allow you to look them up in your dashboard, unless you ask us
        to delete them earlier.
      </p>

      <h2>6. Security</h2>
      <p>
        We implement reasonable technical and organisational measures &mdash;
        including TLS in transit, encryption at rest for sensitive fields,
        least-privilege access, audit logging, and periodic reviews &mdash; to
        protect your data. No system is completely secure, and we cannot
        guarantee absolute security, but we treat lapses as serious incidents
        and will notify you and the authorities where required.
      </p>

      <h2>7. Your rights</h2>
      <p>Under the DPDP Act and applicable law, you have the right to:</p>
      <ul>
        <li>Access the personal data we hold about you.</li>
        <li>Ask us to correct inaccurate data.</li>
        <li>
          Request deletion of your data (subject to obligations that require us
          to retain it, e.g., tax records).
        </li>
        <li>Nominate a person to exercise your rights in the event of your death or incapacity.</li>
        <li>Withdraw consent where consent is the basis of processing.</li>
        <li>File a complaint with the Data Protection Board of India.</li>
      </ul>
      <p>
        To exercise any of these rights, write to{" "}
        <a href="mailto:privacy@advoset.in">privacy@advoset.in</a>.
      </p>

      <h2>8. Children</h2>
      <p>
        Advoset is not intended for children under 18. We do not knowingly
        collect data of minors. If you believe a minor has provided us data,
        please write to us and we will remove it.
      </p>

      <h2>9. Cookies</h2>
      <p>
        We use strictly-necessary cookies for authentication and session
        management, and lightweight analytics cookies to understand how the
        product is used. You can clear cookies from your browser settings; this
        may sign you out.
      </p>

      <h2>10. Changes to this policy</h2>
      <p>
        We may update this policy from time to time. Material changes will be
        notified through the platform or by email. The &quot;Last updated&quot;
        date above always reflects the current version.
      </p>

      <h2>11. Contact &amp; Grievance Officer</h2>
      <p>
        For privacy questions or to exercise your rights, write to{" "}
        <a href="mailto:privacy@advoset.in">privacy@advoset.in</a>. Our
        Grievance Officer, contactable at{" "}
        <a href="mailto:grievance@advoset.in">grievance@advoset.in</a>,
        acknowledges complaints within 48 hours and resolves them within 15
        days.
      </p>
    </LegalPageLayout>
  );
}
